import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChart } from './hooks/useChart';
import { useCrosshair } from './hooks/useCrosshair';
import PeriodSelector from './components/PeriodSelector';
import CrosshairInfo from './components/CrosshairInfo';
import { useFetchKineData } from './hooks/useFetchKlineData';
import { UTCTimestamp } from 'lightweight-charts';
import { useTheme } from '@/components/ThemeProvider';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { IKlineReq } from '@/request/kline/kline.request.interface';
import { KlinePeriodTime } from './config';
import { calculateTimeRange, updateKLineWithWsData } from './util';
import { Loader2 } from 'lucide-react';
import { useWebSocket } from '@/context/websocketContext';
import { IKline } from '@/request/kline/kline.response.interface';
import klineApiService from '@/request/kline/kline.api.service';
import { useAppStore } from '@/store';
import { useTradeStore } from '../../store';
export default () => {
  const {
    chainInfo: { path },
    chainTokenAddressMap,
  } = useAppStore();
  const { theme } = useTheme();
  const { address } = useParams();
  const { klinePeriod, setKlinePeriod } = useTradeStore();
  const tokenAddress = address || chainTokenAddressMap[path];
  const containerRef = useRef<HTMLDivElement>(null);
  const [fetchParams, setFetchParams] = useState<IKlineReq>({ period: klinePeriod, tokenAddress: '' });
  const isInitialLoad = useRef(true);
  const { chart, candlestickSeries, scrollToRealTime } = useChart(containerRef);
  const crosshairData = useCrosshair(chart, candlestickSeries);
  const { klineData, setKlineData, loading } = useFetchKineData(fetchParams);
  const { data: wsData, isConnected } = useWebSocket();

  const chartStyles = useMemo(
    () => ({
      backgroundColor: theme === 'light' ? '#FFFFFF' : '#0b0b0b59',
      borderColor: theme === 'light' ? '#eee' : '#27272a',
      watermarkColor: theme === 'light' ? '#eee' : '#353535',
    }),
    [theme],
  );

  const transformKlineData = useCallback(
    (rawData: IKline[]) =>
      rawData.map((item) => ({
        close: Number(item.close),
        high: Number(item.high),
        low: Number(item.low),
        open: Number(item.open),
        time: Math.floor(item.timestamp) as UTCTimestamp,
      })),
    [],
  );

  const handleWebSocketUpdate = useCallback(() => {
    if (!candlestickSeries || !wsData?.data) return;

    if (wsData.event === 'klineUpdate') {
      const klineData = candlestickSeries.data();
      const lastKLine = klineData[klineData.length - 1];

      if (lastKLine) {
        const updatedCandle = updateKLineWithWsData(lastKLine, wsData.data, fetchParams.period as KlinePeriodTime);
        candlestickSeries.update(updatedCandle);
      }
    }
  }, [isConnected, wsData, candlestickSeries, fetchParams.period, scrollToRealTime]);

  const loadHistoricalData = useCallback(async () => {
    try {
      if (!candlestickSeries) return;
      const oldData = candlestickSeries.data();
      const res = await klineApiService.getKlineApi({
        tokenAddress: fetchParams.tokenAddress,
        period: fetchParams.period,
        ...calculateTimeRange(Number(oldData[0].time), fetchParams.period as KlinePeriodTime),
      });

      if (res.code === 0) {
        const historyList = res.data.slice(0, -1);
        const historyCandleData = transformKlineData(historyList);
        const collectData = [...historyCandleData, ...oldData];
        candlestickSeries.setData(collectData);
      }
    } catch (error) {
      console.error(error);
    }
  }, [tokenAddress, fetchParams.period, candlestickSeries, transformKlineData]);

  const onPeriodChange = (e: string) => {
    isInitialLoad.current = true;
    setKlineData([]);
    setFetchParams({
      tokenAddress,
      period: e as KlinePeriodTime,
    });
    setKlinePeriod(e as KlinePeriodTime);
  };

  useEffect(handleWebSocketUpdate, [isConnected, wsData, candlestickSeries, fetchParams.period]);

  useEffect(() => {
    if (tokenAddress) {
      setKlineData([]);
      isInitialLoad.current = true;
      setFetchParams({ tokenAddress, period: klinePeriod });
    }
  }, [tokenAddress, klinePeriod]);

  useEffect(() => {
    if (!candlestickSeries || !klineData.length) return;

    const candleData = transformKlineData(klineData);
    // const volumeData = klineData.map((item) => ({
    //   time: item.timestamp as any,
    //   value: Number(item.volume),
    //   color: item.close >= item.open ? '#EA39435d' : '#16C7825d',
    // }));

    chart?.applyOptions({
      layout: { background: { color: chartStyles.backgroundColor }, textColor: '#a1a1aa' },
      grid: { vertLines: { color: chartStyles.borderColor }, horzLines: { color: chartStyles.borderColor } },
      rightPriceScale: { borderColor: chartStyles.borderColor },
      watermark: { color: chartStyles.watermarkColor },
    });

    // volumeSeries?.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

    // volumeSeries?.setData(volumeData);
    candlestickSeries.setData(candleData);

    if (isInitialLoad.current) {
      chart?.timeScale().setVisibleLogicalRange({ from: candleData.length - 50, to: candleData.length });
      isInitialLoad.current = false;
    }
  }, [klineData, candlestickSeries, theme, transformKlineData, chartStyles]);

  useEffect(() => {
    if (!chart || !candlestickSeries) return;

    const handleRangeChange = debounce(async (range: any) => {
      if (!range || range.to > 30) return;
      await loadHistoricalData();
    }, 300);

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
    };
  }, [chart, klineData, candlestickSeries, tokenAddress, fetchParams.period]);

  return (
    <div className="my-[20px]">
      <div className="w-full flex gap-x-[8px] text-secondaryTextColor mb-[10px]">
        <PeriodSelector period={fetchParams.period as KlinePeriodTime} onPeriodChange={onPeriodChange} />
      </div>
      <div className="relative overflow-hidden rounded-[12px]">
        <div ref={containerRef} className="w-full mx-auto h-[195px]  overflow-hidden rounded-[12px]" />
        {crosshairData && <CrosshairInfo data={crosshairData} />}
        {loading && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 z-10">
            <Loader2 className="size-[50px] text-secondaryTextColor animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};
